
import './../App.css';
import "./../AQWpackages.css";
import QuoteBox from './../QuoteBoxComponent'; // Adjust path as needed
import ConfettiModalWidget from '../ConfettiComponent';
import PackagesComponent from '../PackagesComponent';
import CaseStudiesComponent from '../CaseStudiesSectionComponent';
import MassBalanceAnimation from '../MassBalanceAnimationComponent';
import SEO from '../SEO';
import React from 'react';
import { createIcons, icons } from 'lucide';
import { useNavigate } from 'react-router-dom';
import { NavHashLink } from 'react-router-hash-link';
import { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SOFTWARE_APPLICATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'AquaWARE',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: 'https://landingpage.aquaware.no',
  description: 'AquaWARE is a digital platform for aquaculture: real-time water quality monitoring, predictive analytics, simulators and automated reporting for fish farming operations.',
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
  },
};

const DATA_FLOW_NODES = [
  { key: 'acq', n: 1, title: 'Data Acquisition', desc: 'Real-time sensor collection', color: '#4f8ef7', rgb: '79,142,247', x: 18, y: 8, hit: '0s',
    icon: (<><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" /></>) },
  { key: 'sto', n: 2, title: 'Data Storage', desc: 'Secure cloud infrastructure for all data', color: '#a259ff', rgb: '162,89,255', x: 82, y: 8, hit: '2s',
    icon: (<><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" /></>) },
  { key: 'ana', n: 3, title: 'Data Analysis', desc: 'Advanced analysis & forecast', color: '#f5c842', rgb: '245,200,66', x: 82, y: 68, hit: '4s',
    icon: (<><path d="M12 18V5" /><path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4" /><path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5" /><path d="M17.997 5.125a4 4 0 0 1 2.526 5.77" /><path d="M18 18a4 4 0 0 0 2-7.464" /><path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517" /><path d="M6 18a4 4 0 0 1-2-7.464" /><path d="M6.003 5.125a4 4 0 0 0-2.526 5.77" /></>) },
  { key: 'res', n: 4, title: 'Automated Response', desc: 'System triggers actions & alerts', color: '#9ED3CD', rgb: '158,211,205', x: 18, y: 68, hit: '6s',
    icon: (<><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" /></>) },
];

// Mobile layout: cards sit in a 3x3 grid (card, gap, card / gap, gap, gap / card, gap, card) so a small
// arrow can occupy the gap cell between each pair, tracing the same clockwise loop as the desktop diagram.
const DATA_FLOW_CONNECTORS = [
  { key: 'right', col: 2, row: 1, rotate: 0 },   // Data Acquisition -> Data Storage
  { key: 'down', col: 3, row: 2, rotate: 90 },   // Data Storage -> Data Analysis
  { key: 'left', col: 2, row: 3, rotate: 180 },  // Data Analysis -> Automated Response
  { key: 'loop', col: 1, row: 2, rotate: 270 },  // Automated Response -> back to Data Acquisition
];

// Clockwise loop legs, in the 1000x500 viewBox of the diagram (start: Data Acquisition, top left)
const DATA_FLOW_LEGS = [
  { d: 'M 240 100 H 750', color: '#4f8ef7', arrow: '756,100 744,93 744,107' },
  { d: 'M 880 100 H 955 Q 975 100 975 120 V 380 Q 975 400 955 400 H 894', color: '#a259ff', arrow: '884,400 896,393 896,407' },
  { d: 'M 760 400 H 250', color: '#f5c842', arrow: '244,400 256,393 256,407' },
  { d: 'M 120 400 H 45 Q 25 400 25 380 V 120 Q 25 100 45 100 H 110', color: '#9ED3CD', arrow: '116,100 104,93 104,107' },
];

function Home() {

    const fishBg = "./images/digitalSalmons.png";
    const optiras = "./images/optiras.jpg";
    const cloud = "./images/aquabank.jpg";
    const aqw = "./images/CVreport_3_wq_2.png";
    const sectionCases = <CaseStudiesComponent />
    const { useState, useEffect } = React;
    const videoSource = './videos/AquaWARE-entrance.mp4';
    const [demoModalOpen, setDemoModalOpen] = useState(false);
    const [config] = useState({
          brand_name: "NovaSoft",
          tagline: "Preventive analysis and quality reports",
          basic_title: "Basic",
          basic_price: "$9",
          pro_title: "Professional",
          pro_price: "$29",
          premium_title: "Premium",
          premium_price: "$79",
          cta_text: "Contact",
          footer_text: "© 2025 NovaSoft. All rights reserved."
        });

      useLayoutEffect(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, []);

      useEffect(() => {
        // Initialize icons, replacing data-lucide tags
        createIcons({ icons });
      }, []);
      
      const navigate = useNavigate();
      const handleNavigation = () => {
        // Perform some action if needed (e.g., form validation, API call)
        console.log("Navigating..."); 
        navigate('/media'); // Redirect to the target path
      };

    return (
        <div>
            <SEO
              title="AquaWARE — Fish Farming Digital Platform"
              description="AquaWARE is Searas' digital platform for aquaculture: real-time water quality monitoring, predictive analytics, simulators and automated reporting for smarter, more sustainable fish farming."
              path="/"
            />
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify(SOFTWARE_APPLICATION_JSON_LD)}
              </script>
            </Helmet>
            <header id="welcome-section" className="hero-bg" style={{ padding: "120px 24px 60px" }}>
              <div style={{ maxWidth: "760px", margin: "0 auto", textAlign: "center" }}>
                <h1  style={{ fontSize: "clamp(36px, 5vw, 62px)", lineHeight: 1.15, margin: "0 0 20px", fontWeight: 400 }}>
                  Beyond sensors<br/>
                </h1>
                <p className="fade-up delay-1" style={{ fontSize: "18px", color: "var(--muted)", lineHeight: 1.7, margin: "0 0 36px" }}>
                  {config.tagline}
                </p>
              </div>

              <div className="fade-up delay-3" style={{ margin: "0 0 20px", borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 20px 40px rgba(0,0,0,0.3)", maxWidth: "1400px", marginLeft: "auto", marginRight: "auto" }}>
                <video width="100%" height="auto" autoPlay muted loop style={{ display: "block", backgroundColor: "#0a0e1a" }}>
                  <source src={videoSource} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>

              <div style={{ maxWidth: "760px", margin: "0 auto", textAlign: "center" }}>
                <div className="badge" style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "18px",
                  fontWeight: 200, letterSpacing: "0.08em", textTransform: "uppercase",
                  padding: "4px 12px", borderRadius: "999px", background: "rgba(158, 211, 205, 0.15)",
                  color: "#9ED3CD", border: "1px solid rgba(158, 211, 205, 0.3)", margin: "20px auto 28px",
                  width: "fit-content" }}>
                  <i data-lucide="zap" style={{ width: "18px", height: "18px" }}></i>
                  <span>Fish Farming Digital Platform</span>
                  <i data-lucide="zap" style={{ width: "18px", height: "18px" }}></i>
                </div>
              </div>

              <div className="fade-up delay-2" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap", marginBottom: "48px" }}>
                <form action="https://www.searas.no/#contact2">
                  <button type="submit" className="btn-primary" style={{ padding: "14px 32px", fontSize: "16px" }}>
                      {config.cta_text}
                  </button>
                </form>
                <button className="btn-ghost" style={{ padding: "14px 32px", fontSize: "16px" }} onClick={() => setDemoModalOpen(true)}>
                    View Demo →
                </button>
              </div>
              
              <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginTop: '1.3em', marginBottom: '1.8em' }}>
                        <QuoteBox
                            quote='Empowering Water System Management with Advanced Analytics.
                                The comprehensive system allows the user to efficiently monitor the entire water system.
                                With its advanced features and artificial intelligence (AI) capabilities,
                                AquaWARE revolutionizes the way you handle and analyze operational data.
                                It seamlessly integrates various components, such as databases, models, and graphical displays,
                                to provide you with valuable insights for informed decision-making.'
                            author="Mia Guttorm Moseng "
                            source= "CCO Searas" />
                </div>
              </div>
              <h1  style={{ fontSize: "clamp(36px, 5vw, 42px)", lineHeight: 1.15, marginTop:"50px", fontWeight: 400 }}>
                 Built for modern aquaculture<br/>
              </h1>
            </header>

            <div className="divider" style={{ margin: "30px auto" }}></div>

            {/* About Section */}
            <section id="about-section" style={{ padding: "72px 24px" }}>
              <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "60px" }}>
                  <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent)", margin: "0 0 10px" }}>ABOUT</p>
                  <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", margin: "0 0 14px", fontWeight: 400 }}>
                    About AquaWARE</h2>
                  <p style={{ color: "var(--muted)", fontSize: "16px", margin: 0 }}>	
                            Predictive analytics for fish growth
                        |	Data-driven insights and decision support
                        |	Simulator for water quality optimization
                        |	Automated reports and economic tools
                        |	Real-Time monitoring and alarms </p>
                </div>

                {/* Three Pillars */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "32px", marginBottom: "60px" }}>
                  {/* Software */}
                  <div className="metric-card" style={{ display: "flex", flexDirection: "column", background: "var(--surface)" }}>
                    <div style={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(158, 211, 205, 0.2)", background: "linear-gradient(135deg, rgba(158,211,205,0.1) 0%, rgba(158,211,205,0.05) 100%)", height: "180px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                      <img src={cloud} alt="Intelligent Software" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }}  />
                    </div>
                    <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9ED3CD", margin: "0 0 8px" }}>SOFTWARE</p>
                    <h3  style={{ fontSize: "22px", fontWeight: 400, margin: "0 0 12px", lineHeight: 1.3 }}>Software Solutions & Digital Services</h3>
                        
                        <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                            Searas Proprietary software for <strong><em>data visualization</em></strong> and <strong><em>analysis</em></strong>, 
                        automatic reporting, forecast and operational support, 
                            <NavHashLink className="App-link-sublte" to="/products"> AquaWARE
                            </NavHashLink>
                        </p>
                        <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                            Searas Proprietary <strong><em>cloud architecture</em></strong> to store fish farming big data, and automatic logging processes,
                            <NavHashLink className="App-link-sublte" to="/products/#detail-cloud"> AquaBANK
                            </NavHashLink>
                        </p>
                        <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                            <strong><em>Simulators</em></strong> and <strong><em>advanced calculators</em></strong>,
                            <NavHashLink className="App-link-sublte" to="/products/#detail-simula"> AquaSIM
                            </NavHashLink>
                        </p>
                                <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>Ubiquity with 
                                  <a
                                        className="App-link-sublte"
                                        target="_blank"
                                        rel="noreferrer"
                                        href="https://www.optiras.no"
                                    > AquaSYNC </a> 
                                     (mobile)
                                    </p>
                        <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                            Flexible <NavHashLink className="App-link-sublte" to="/products/#detail-integrity2">integration</NavHashLink>, adaptable to any system
                        </p>
                        <NavHashLink to="/products" className='learn_more_blue'> Learn More about Software</NavHashLink>
                        
                  </div>

                  {/* Support */}
                  <div className="metric-card" style={{ display: "flex", flexDirection: "column", background: "var(--surface)" }}>
                    <div style={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(79, 142, 247, 0.2)", background: "linear-gradient(135deg, rgba(79,142,247,0.1) 0%, rgba(79,142,247,0.05) 100%)", height: "180px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                      <img src={fishBg} alt="Expert Support" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                    <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#4f8ef7", margin: "0 0 8px" }}>SUPPORT</p>
                    <h3 style={{ fontSize: "22px", fontWeight: 400, margin: "0 0 12px", lineHeight: 1.3 }}>Expert advice in Operational performance</h3>
                    <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                        
                        We provide dedicated support tailored to your farm's unique needs and goals.
                    </p>
                    <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                      <strong><em>Technical support</em></strong>, consulting services with 
                                    <a
                                        className="App-link-sublte"
                                        target="_blank"
                                        rel="noreferrer"
                                        href="https://www.searas.no/aquasense"
                                    >  AquaCARE</a>
                                </p>
                    <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                      <strong><em>Simulations on demand</em></strong> : Optimal feeding, Growth targets, Alkalinity, Stress, CO2 emissions, Degassing capabilities...</p>
                                <p className="serif"  style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                                  <strong><em>Technical reports</em></strong>, Batch Performance KPIs, case studies, subscriptions</p>
                                <p className="serif"  style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                                  Monitoring Control Center with 
                                    <a 
                                    className="App-link-sublte" 
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://www.searas.no/aquasense"
                                    > AquaSENSE </a> 
                                </p>

                    <div style={{ marginTop: "auto" }}><a href="#" className='learn_more_peach'> Learn More about Support </a></div>
                  </div>

                  {/* Integration */}
                  <div className="metric-card" style={{ display: "flex", flexDirection: "column", background: "var(--surface)" }}>
                    <div style={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255, 173, 96, 0.2)", background: "linear-gradient(135deg, rgba(255,173,96,0.1) 0%, rgba(255,173,96,0.05) 100%)", height: "180px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                      <img src={optiras} alt="Seamless Integration" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                    <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#FFAD60", margin: "0 0 8px" }}>INNOVATION</p>
                    <h3  style={{ fontSize: "22px", fontWeight: 400, margin: "0 0 12px", lineHeight: 1.3 }}>Continuous Development & New Technologies</h3>
                                <p style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>i-RAS, autopilot for 
                                    <a
                                        className="App-link-sublte"
                                        target="_blank"
                                        rel="noreferrer"
                                        href="https://www.optiras.no"
                                    > OptiRAS</a>
                                </p>
                        <p className="serif" style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>
                            Acoustics technology by <NavHashLink className="App-link-sublte" to="/products/#detail-dev"> AquaNODE </NavHashLink>
                                  for soundscape, stress evaluation, biomass estimation and feed optimization 
                                </p>
                                <p style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>Machine Learning for predictive analysis, forecast and Smart Alarms system</p>
                                <p style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}><span style={{ color: "white" }}>TIC</span> and <span style={{ color: "white" }}>Nephrocalcinosis risk</span> models</p>
                                <p style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>Biofilter efficiency and NH₃ risk</p>
                                <p style={{ color: "var(--muted)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>Hydraulics Simulations</p>
                        <a href="#" className='learn_more_blue2'> Learn More about R&D</a>
                  </div>

                </div>

                {/* Story Section */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "48px", alignItems: "center" }}>
                  <div>
                    <div style={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", 
                      background: "linear-gradient(135deg, rgba(79,142,247,0.1) 0%, rgba(162,89,255,0.1) 100%), linear-gradient(var(--surface), var(--surface))", 
                      height: "360px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <img src={aqw} alt="Seamless Integration" loading="lazy" style={{ width: "100%", height: "90%", objectFit: "cover" }} />
                    </div>
                  </div>

                  <div>
                    <h3 style={{ fontSize: "24px", fontWeight: 400, margin: "0 0 16px", lineHeight: 1.3, paddingTop: 0, textAlign: "left" }}>Our mission: Smarter farming, better yields</h3>
                    <p className="serif"  style={{ color: "var(--muted)", fontSize: "15px", lineHeight: 1.7, margin: "0 0 16px" }}>
                      Full control over water quality.
                    </p>
                    <p className="serif"  style={{ color: "var(--muted)", fontSize: "15px", lineHeight: 1.7, margin: "0 0 16px" }}>
                      Degassing measured and mass balance can be understood
                    </p>
                    <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontSize: "28px", fontWeight: 700, color: "#9ED3CD", margin: "0 0 4px" }}>20+</div>
                        <div style={{ fontSize: "13px", color: "var(--muted)" }}>Active Farms</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "28px", fontWeight: 700, color: "#FFAD60", margin: "0 0 4px" }}>95%</div>
                        <div style={{ fontSize: "13px", color: "var(--muted)" }}>Satisfaction Rate</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "28px", fontWeight: 700, color: "#4f8ef7", margin: "0 0 4px" }}>2M+</div>
                        <div style={{ fontSize: "13px", color: "var(--muted)" }}>Fish Monitored</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="divider" style={{ margin: "60px auto" }}></div>

      {/* Services Section with Interactive Diagram */}
            <section id="services-section" className="background-grain" style={{ padding: "72px 24px" }}>
                {/* <div className='center-content'  >
                    <div>
                        <h1>Services, Data Flow and Functionalities</h1>
                    </div>

                    <div className='card_press'>
                      <div style={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", 
                        background: "linear-gradient(135deg, rgba(79,142,247,0.1) 0%, rgba(162,89,255,0.1) 100%), linear-gradient(var(--surface), var(--surface))", 
                        height: "360px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img src={aqw_biomass} alt="Seamless Integration" loading="lazy" style={{ width: "100%", height: "90%", objectFit: "cover" }} />
                      </div>
                    </div>
                </div> */}
            
              <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "60px" }}>
                  <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent)", margin: "0 0 10px" }}>OUR SERVICES</p>
                  <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", margin: "0 0 14px", fontWeight: 400 }}>How it works, Data Flow & System Architecture</h2>
                  <p style={{ color: "var(--muted)", fontSize: "16px", margin: 0 }}>Real-time data processing powering your aquaculture decisions.</p>
                </div>

                <style>{`
                  .df-wrapper { display:flex; align-items:center; justify-content:center; }
                  .df-flow { position:relative; width:100%; max-width:1000px; aspect-ratio:2/1; container-type:inline-size; }
                  .df-svg { position:absolute; inset:0; width:100%; height:100%; }
                  .df-cap { font-size:13px; font-weight:600; text-anchor:middle; letter-spacing:.02em; }
                  .df-center { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); padding:8px 18px; border-radius:999px;
                    background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.14); color:var(--muted);
                    font-size:clamp(11px,1.5cqw,14px); font-weight:600; letter-spacing:.06em; text-transform:uppercase; white-space:nowrap; }
                  .df-node { position:absolute; transform:translateX(-50%); width:17cqw; display:flex; flex-direction:column; align-items:center; gap:1.6cqw;
                    transition: transform .4s cubic-bezier(.34,1.56,.64,1); cursor:pointer; }
                  .df-node:hover { transform:translateX(-50%) scale(1.08) translateY(-8px); }
                  .df-icon-wrap { position:relative; }
                  .df-box { width:12cqw; height:12cqw; border-radius:2cqw; border:2px solid var(--c); display:flex; align-items:center; justify-content:center; color:var(--c);
                    background: linear-gradient(135deg, rgba(var(--rgb),.3) 0%, rgba(var(--rgb),.1) 100%), linear-gradient(var(--surface), var(--surface));
                    box-shadow:0 8px 24px rgba(var(--rgb),.15), inset 0 0 20px rgba(var(--rgb),.1);
                    animation: dfHit 8s ease-out infinite; animation-delay: var(--hit); }
                  .df-ico { width:4.8cqw; height:4.8cqw; }
                  .df-badge { position:absolute; top:-.9cqw; left:-.9cqw; width:2.6cqw; height:2.6cqw; min-width:22px; min-height:22px; border-radius:50%;
                    background:var(--c); color:#101828; font-weight:700; font-size:max(12px,1.35cqw); display:flex; align-items:center; justify-content:center;
                    box-shadow:0 2px 8px rgba(0,0,0,.4); }
                  .df-label { text-align:center; }
                  .df-label h4 { margin:0 0 6px; font-size:max(12px,1.4cqw); font-weight:600; color:var(--text); }
                  .df-label p { margin:0; font-size:max(11px,1.2cqw); color:var(--muted); line-height:1.4; }
                  @keyframes dfHit {
                    0%   { box-shadow:0 0 0 0 rgba(var(--rgb),.7), 0 8px 24px rgba(var(--rgb),.15), inset 0 0 20px rgba(var(--rgb),.1); animation-timing-function:ease-out; }
                    5%   { box-shadow:0 0 0 14px rgba(var(--rgb),0), 0 12px 40px rgba(var(--rgb),.6), inset 0 0 36px rgba(var(--rgb),.45); animation-timing-function:ease-in-out; }
                    30%  { box-shadow:0 0 0 14px rgba(var(--rgb),0), 0 9px 27px rgba(var(--rgb),.22), inset 0 0 23px rgba(var(--rgb),.16); animation-timing-function:ease-in-out; }
                    45%  { box-shadow:0 0 0 14px rgba(var(--rgb),0), 0 8px 24px rgba(var(--rgb),.15), inset 0 0 20px rgba(var(--rgb),.1); }
                    100% { box-shadow:0 0 0 14px rgba(var(--rgb),0), 0 8px 24px rgba(var(--rgb),.15), inset 0 0 20px rgba(var(--rgb),.1); }
                  }
                  @keyframes dfDash { to { stroke-dashoffset:-20; } }
                  .df-dash { animation: dfDash 1s linear infinite; }
                  @keyframes dfHop {
                    0%   { opacity:0; offset-distance:0%; }
                    1%   { opacity:1; }
                    23%  { opacity:1; }
                    25%  { opacity:0; offset-distance:100%; }
                    100% { opacity:0; offset-distance:100%; }
                  }
                  .df-pkt { animation: dfHop 8s linear infinite; opacity:0; }
                  .df-arrow { display:none; }
                  @media (max-width: 640px) {
                    .df-wrapper { padding:24px !important; }
                    .df-flow { aspect-ratio:auto; container-type:normal; display:grid;
                      grid-template-columns: 1fr 28px 1fr; grid-template-rows: auto 28px auto; align-items:center; }
                    .df-svg, .df-center { display:none; }
                    .df-node { position:static !important; transform:none; width:auto; }
                    .df-node:hover { transform:none; }
                    .df-box { width:96px; height:96px; border-radius:18px; }
                    .df-ico { width:40px; height:40px; }
                    .df-badge { width:24px; height:24px; top:-8px; left:-8px; }
                    .df-label h4 { font-size:14px; }
                    .df-label p { font-size:12px; }
                    .df-arrow { display:flex; align-items:center; justify-content:center; color:var(--muted); opacity:0.6; }
                    .df-arrow svg { width:18px; height:18px; }
                  }
                  @media (prefers-reduced-motion: reduce) {
                    .df-box, .df-dash, .df-pkt { animation:none; }
                    .df-pkt { opacity:.8; }
                  }
                `}</style>

                {/* Data flow loop: starts at Data Acquisition (top left) and runs clockwise */}
                <div className="df-wrapper" style={{ background: "linear-gradient(135deg, rgba(79,142,247,0.08) 0%, rgba(162,89,255,0.08) 100%), linear-gradient(var(--surface), var(--surface))", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "24px", padding: "40px 40px 110px", marginBottom: "60px" }}>
                  <div className="df-flow">
                    <svg className="df-svg" viewBox="0 0 1000 500" role="img" aria-label="Data flow loop, clockwise from the top left: 1 Data Acquisition sends sensor readings to 2 Data Storage, which sends stored data to 3 Data Analysis, which sends insights and predictions to 4 Automated Response, whose alerts and actions go back to the farm.">
                      {DATA_FLOW_LEGS.map((leg, k) => (
                        <g key={k}>
                          <path d={leg.d} fill="none" stroke={leg.color} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="10 10" opacity="0.45" className="df-dash" />
                          <polygon points={leg.arrow} fill={leg.color} opacity="0.9" />
                          {[5.5, 4.5, 3.5].map((r, j) => (
                            <g key={j} className="df-pkt" style={{ offsetPath: `path('${leg.d}')`, animationDelay: `${(k * 2 + j * 0.16).toFixed(2)}s` }}>
                              <circle r={r} fill={leg.color} opacity={[1, 0.75, 0.5][j]} />
                            </g>
                          ))}
                        </g>
                      ))}
                      <text x="500" y="88" className="df-cap" fill="#4f8ef7">sensor readings</text>
                      <text x="500" y="374" className="df-cap" fill="#f5c842">insights &amp; predictions</text>
                      <text transform="translate(958 250) rotate(90)" className="df-cap" fill="#a259ff">stored data</text>
                      <text transform="translate(42 250) rotate(-90)" className="df-cap" fill="#9ED3CD">alerts &amp; actions</text>
                    </svg>
                    <div className="df-center">Continuous data loop</div>
                    {DATA_FLOW_NODES.map((node) => (
                      <div key={node.key} className="df-node" style={{ left: `${node.x}%`, top: `${node.y}%`, gridColumn: node.x < 50 ? 1 : 3, gridRow: node.y < 50 ? 1 : 3, '--c': node.color, '--rgb': node.rgb, '--hit': node.hit } as React.CSSProperties}>
                        <div className="df-icon-wrap">
                          <div className="df-box">
                            <svg className="df-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{node.icon}</svg>
                          </div>
                          <span className="df-badge">{node.n}</span>
                        </div>
                        <div className="df-label">
                          <h4>{node.title}</h4>
                          <p>{node.desc}</p>
                        </div>
                      </div>
                    ))}
                    {DATA_FLOW_CONNECTORS.map((c) => (
                      <div key={c.key} className="df-arrow" style={{ gridColumn: c.col, gridRow: c.row }} aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: `rotate(${c.rotate}deg)` }}>
                          <path d="M5 12h13" />
                          <path d="M13 6l6 6-6 6" />
                        </svg>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Service Details Grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gridAutoRows: "1fr", gap: "24px" }}>
                  {[
                    { title: "Real-Time Monitoring", desc: "24/7 sensor tracking with sub-minute latency", icon: "activity", color: "#4f8ef7", bg: "rgba(79,142,247,0.12)" },
                    { title: "Smart Alarms", desc: "Get ahead of problems before they impact yield", icon: "alert-circle", color: "#a259ff", bg: "rgba(162,89,255,0.12)" },
                    { title: "Custom Dashboards", desc: "Visualize what matters most to your operation", icon: "layout-grid", color: "#FFAD60", bg: "rgba(255, 173, 96, 0.15)" },
                    { title: "Mass Balance", desc: "Keep track of your environmental quotas, emissions and discharges", icon: "scale", color: "#9ED3CD", bg: "rgba(158,211,205,0.12)" },
                    { title: "Flexible Integration", desc: "Adaptable to any system", icon: "network", color: "#4f8ef7", bg: "rgba(79,142,247,0.12)" },
                    { title: "Forecast", desc: "Predictive analysis", icon: "trending-up-down", color: "#FFAD60", bg: "rgba(255, 173, 96, 0.15)" },
                    { title: "Simulators", desc: "What can you expect under certain conditions given", icon: "calculator", color: "#a259ff", bg: "rgba(162,89,255,0.12)" },
                    { title: "Control Center", desc: "Remote control and surveillance services", icon: "cctv", color: "#9ED3CD", bg: "rgba(158,211,205,0.12)" },
                    { title: "Reanalysis - ML", desc: "Coninuous reanalysis and system training", icon: "brain-circuit", color: "#9ED3CD", bg: "rgba(158,211,205,0.12)" },
                  ].map((service, idx) => (
                    <div key={idx} style={{ background: "var(--surface)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "16px", padding: "22px 26px", transition: "all 0.3s", display: "flex", alignItems: "center", gap: "18px" }}>
                      <div style={{ width: "50px", height: "50px", borderRadius: "12px", background: service.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i data-lucide={service.icon} style={{ width: "24px", height: "24px", color: service.color }}></i>
                      </div>
                      <div style={{ textAlign: "center", flex: 1 }}>
                        <h3 style={{ fontSize: "17px", fontWeight: 600, margin: "0 0 4px", color: "var(--text)", paddingTop: 0 }}>{service.title}</h3>
                        <p style={{ fontSize: "14px", color: "var(--muted)", margin: 0, lineHeight: 1.5 }}>{service.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <div className="divider" style={{ margin: "60px auto" }}></div>

            <section>

                  <div>
                    <MassBalanceAnimation/>
                  </div>

            </section>

            <div className="divider" style={{ margin: "60px auto" }}></div>

            <section style={{ marginTop: '30px'}} id="packages-section">
                
                    <div>
                        <PackagesComponent />
                    </div>
               
            </section>

            <div className="divider" style={{ margin: "60px auto" }}></div>
      
      {/* Case Studies and news */}
            <div>
                {sectionCases}
                <div className="fade-up delay-2" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap", marginBottom: "48px" }}>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: '48px', marginBottom: "30px" }}>
                      {" "}
                      <button
                        onClick={handleNavigation}
                        className="btn-primary"
                        style={{ padding: "14px 32px", fontSize: 15 }}
                      >
                        Visit the Library →
                      </button>{" "}
                  </div>
                </div>
            </div>

      <div className="divider" style={{ margin: "60px auto" }}></div>
      
      {/* Contact Form Section */}
            <section id="contact-section" style={{ position: 'relative', padding: "72px 24px" }}> 
                <div style={{ maxWidth: "600px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                  <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent)", margin: "0 0 10px" }}>GET IN TOUCH</p>
                  <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", margin: "0 0 14px", fontWeight: 400 }}>Let's chat about your needs</h2>
                  <p style={{ color: "var(--muted)", fontSize: "16px", margin: 0 }}>Our team will get back to you within 24 hours.</p>
                </div>
                <form action="https://www.searas.no/#contact2">
                  <button type="submit" className="btn-primary" style={{ width: "30%", padding: "14px 24px", fontSize: "16px", marginTop: "12px", height: "48px" }}>
                    Send Message
                  </button>
                </form>
                  </div>
            </section>

            <div className="divider" style={{ margin: "60px auto" }}></div>

            {/* Our Coders Section */}
            <section style={{ padding: "72px 24px" }}>
              <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                  <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent)", margin: "0 0 10px" }}>// team</p>
                  <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", margin: "0 0 14px", fontWeight: 400 }}>Our Coders</h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 340px))", gap: 24, justifyContent: "center" }}>
                  {[
                    { name: "Jan Vidar Nordstrand", role: "System Architect", photo: "/team/JanVidar.jpg" },
                    { name: "Trygve Sandven Håland", role: "Software Developer - Database & APIs", photo: "/team/Trygve.jpg" },
                    { name: "Andreas Toen Øksnes", role: "Senior Software Engineer - FullStack", photo: "/team/Andreas.jpg" },
                    { name: "Sindre Wangsholm", role: "Software Engineer - FullStack", photo: "/team/Sindre.jpg" },
                    { name: "Sasha Bylinkin", role: "Software Developer - Data Science, ML & AI", photo: "/team/Sasha.jpg" },
                    { name: "Sebastian Bekkvik Aas", role: "Junior Software Engineer", photo: "/team/Seb.jpg" },
                    { name: "Sara Queralt", role: "Chief SW-Development Officer", photo: "/team/Sara.jpg" },
                  ].map((member, i) => (
                    <div key={i} className="plan-card" style={{ padding: 28, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
                      <div style={{
                        width: 72, height: 72, borderRadius: "50%",
                        overflow: "hidden",
                        background: "rgba(158,211,205,0.08)", border: "1px dashed rgba(158,211,205,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {member.photo
                          ? <img src={member.photo} alt={member.name ?? ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          : <i data-lucide="user" style={{ width: 28, height: 28, color: "rgba(158,211,205,0.4)" }}></i>
                        }
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <p style={{ margin: "0 0 4px", fontSize: 14, fontFamily: "'JetBrains Mono', monospace",
                          color: member.name ? "var(--text)" : "rgba(255,255,255,0.2)" }}>
                          {member.name ?? "Name placeholder"}
                        </p>
                        <p style={{ margin: 0, fontSize: 12,
                          color: member.role ? "var(--muted)" : "rgba(255,255,255,0.15)" }}>
                          {member.role ?? "Role placeholder"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <footer>
                <div className='center-content' >
                    <div className="tooltip-container">
                        <div className="tooltip">
                            <div className="profile">
                                <div className="user">
                                    <div className="img">Ui</div>
                                    <div className="details">
                                        <div className="name">Searas</div>
                                        <div className="username">@searasas</div>
                                    </div>
                                </div>
                                <div className="about">500+ Connections</div>
                            </div>
                        </div>
                        <div className="text">
                            <a className="icon" href="https://no.linkedin.com/company/searas">
                                <div className="layer">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span className="fab fa-linkedin">
                                        <svg viewBox="0 0 448 512" height="1em">
                                            <path
                                                d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"
                                            ></path>
                                        </svg>
                                    </span>
                                </div>
                                <div className="text">LinkedIn</div>
                            </a>
                        </div>
                    </div>
                </div>
                </footer>
{/*             <section>
                <MyParallaxContainer 
                    imageSrc={fishBg}
                    CompToRender={<ContactFormComponent />}
                    CompToRender2={<VideoComponent />}

                />
            </section> */}

            {demoModalOpen && (
                <div
                    onClick={() => setDemoModalOpen(false)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 1000,
                        background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: "24px",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: "relative", width: "100%", maxWidth: "860px",
                            background: "#0a0e1a", borderRadius: "16px",
                            border: "1px solid rgba(255,255,255,0.12)",
                            boxShadow: "0 32px 64px rgba(0,0,0,0.6)",
                            overflow: "hidden",
                        }}
                    >
                        <button
                            onClick={() => setDemoModalOpen(false)}
                            style={{
                                position: "absolute", top: "12px", right: "14px", zIndex: 10,
                                background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%",
                                width: "32px", height: "32px", cursor: "pointer",
                                color: "#fff", fontSize: "18px", lineHeight: 1,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                            aria-label="Close demo"
                        >
                            ×
                        </button>
                        <video
                            width="100%"
                            height="auto"
                            controls
                            autoPlay
                            style={{ display: "block", backgroundColor: "#0a0e1a" }}
                        >
                            <source src="./videos/AQW_video_web.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            )}
        </div>
    );

};
export default Home;